import type { GenerateInstallationPlanBody } from '@si/types';
import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { useCallback } from 'react';

import { keycloak } from '../keycloak';
import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
}

export function useGenerateInstallationPlan({ projectId }: Params) {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({
      filename,
      plans,
      includeInstallationInstructions,
    }: GenerateInstallationPlanBody) =>
      post({
        url: `/projects/${projectId}/documentation/installation-plans`,
        body: {
          filename,
          language: i18next.language,
          plans,
          includeInstallationInstructions,
          token: keycloak.token,
        },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationPlans(projectId),
      }),
  });

  const generateInstallationPlan = useCallback(
    (params: GenerateInstallationPlanBody) => mutate(params),
    [mutate]
  );

  return {
    generateInstallationPlan,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
